import { ApolloQueryResult } from 'apollo-client';

import { BANNERS_IN_GROUP_LIMIT } from '../../constants';
import {
    adsInternalQueryForEdit,
    adsQueryForEdit,
    baseInfoForBannerCreationQuery,
    creativesQuery,
    getContentPromotionMetadata,
    getGeoByAddressQuery,
    getGeoByCoordinatesQuery,
    getLastChangedAdsQuery,
    getMetroByCoordinatesQuery,
    imagesQueryForEdit,
    internalTemplatePlacesQuery,
    internalTemplateResourcesQuery,
    performanceCreativesQuery,
    recommendationsAdsGroupEditQuery,
    validatePrice,
    vCardsQuery
} from '../../graphql/queries/grid/domain';
import {
    mapApiResponseToBannersData,
    mapApiResponseToBannersDataForCopyPrevEditedBanners,
    mapApiResponseToBaseInfoForBannerCreationData,
    mapCreativeForEditQueryResultToQueryResponse,
    mapImagesForEditQueryResultToQueryResponse,
    mapVCardsForEditQueryResultToQueryResponse
} from '../../mappers/banners-edit/api-data-mappers';
import {
    mapApiResponseToInternalBannersData,
    mapApiResponseToPlaceTemplates,
    mapApiResponseToTemplateResources
} from '../../mappers/banners-edit/banner-edit-internal/banner-edit-internal';
import { mapBannerImageTypeForGalleryItems } from '../../mappers/banners-edit/state-data-mappers/state-data-mappers';
import { BannerId, BannerPrice } from '../../typings/banners';
import { CanvasPageType } from '../../typings/banners';
import {
    bannerAdvTypesHasCopyPrevEditedBanner,
    EditedBannerGroup,
    GetPerformanceCreativesParams
} from '../../typings/bannersEditState';
import { MeasurerSystem } from '../../typings/common';
import { AdvType, AdvTypeClient, BannerImageSize, CampaignId } from '../../typings/domain';
import {
    BannersQueryForEdit,
    BaseInfoForBannerCreationQuery,
    CreativesQuery,
    GdAdGroupOrderByFieldInput,
    GdAdOrderByFieldInput,
    GdCampaignOrderByFieldInput,
    GdContentPromotionType,
    GdCreativeTypeInput,
    GdInternalTemplateInfoRequestEntryInput,
    GdStatPresetInput,
    GetContentPromotionMetadata,
    GetGeoByAddress,
    GetGeoByCoordinates,
    GetLastChangedAds,
    GetMetroByCoordinates,
    ImagesQueryForEdit,
    InternalBannersQueryForEdit,
    InternalTemplatePlacesQuery,
    InternalTemplateResourcesQuery,
    OrderInput,
    PerformanceCreativesQuery,
    PlacementTypeInput,
    RecommendationsAdsGroupEditQuery,
    validateAdPrice,
    VCardsForBannersEdit
} from '../../typings/graphql-grid';
import { GroupId } from '../../typings/groups';
import {
    InternalBannersEditData,
    InternalPlaceTemplates,
    InternalTemplateResources
} from '../../typings/internalBanners';
import { BaseDataProvider } from '../BaseDataProvider/BaseDataProvider';

const mapToAdsApiInput = (bannersIds: BannerId[], campaignsIds: CampaignId[]) => {
    return {
        filter: {
            adIdIn: bannersIds.map(item => Number(item)),
            campaignIdIn: campaignsIds.map(item => Number(item))
        },
        statRequirements: { preset: 'TODAY' }, // сейчас это обязательное поле
        limitOffset: {
            offset: 0,
            limit: 10000
        },
        orderBy: {
            field: GdAdOrderByFieldInput.ID,
            order: OrderInput.ASC
        }
    };
};

const mapToCampaignsInput = (campaignsIds: CampaignId[]) => {
    return {
        filter: {
            campaignIdIn: campaignsIds.map(item => Number(item))
        },
        statRequirements: { preset: 'TODAY' }, // сейчас это обязательное поле
        limitOffset: {
            offset: 0,
            limit: 10000
        },
        orderBy: {
            field: GdAdOrderByFieldInput.ID,
            order: OrderInput.ASC
        }
    };
};

const mapToValidateBannerPriceInput = (bannerPrice: BannerPrice) => {
    return {
        price: bannerPrice.price,
        priceOld: bannerPrice.priceOld || null,
        currency: bannerPrice.currency
    };
};

export class BannersDataProvider extends BaseDataProvider {
    public getBanners = (
        login: string,
        bannersIds: BannerId[],
        campaignsIds: CampaignId[],
        placementType: PlacementTypeInput = PlacementTypeInput.OUTDOOR
    ) => {
        const query = this.createQuery<BannersQueryForEdit>(adsQueryForEdit, {
            variables: {
                adsInput: mapToAdsApiInput(bannersIds, campaignsIds),
                campaignsInput: mapToCampaignsInput(campaignsIds),
                login,
                placementsInput: { placementType }
            }
        });

        return query.then(mapApiResponseToBannersData);
    };

    public getBaseInfoForBannerCreation = (login: string, campaignsIds: CampaignId[], groupsIds: GroupId[]) => {
        const query = this.createQuery<BaseInfoForBannerCreationQuery>(baseInfoForBannerCreationQuery, {
            variables: {
                login,
                adGroupInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.LAST_30DAYS
                    },
                    orderBy: [
                        {
                            field: GdAdGroupOrderByFieldInput.CAMPAIGN_ID,
                            order: OrderInput.DESC
                        }
                    ],
                    limitOffset: {
                        offset: 0,
                        limit: 1
                    },
                    filter: {
                        campaignIdIn: campaignsIds,
                        adGroupIdIn: groupsIds
                    }
                },
                campaignInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.TODAY
                    },
                    // Поумолчанию сортировка ставится по id ASC
                    orderBy: [
                        {
                            field: GdCampaignOrderByFieldInput.ID,
                            order: OrderInput.DESC
                        }
                    ],
                    // По умолчанию лимит ставится 100
                    limitOffset: {
                        offset: 0,
                        limit: 1
                    },
                    filter: {
                        campaignIdIn: campaignsIds,
                        archived: false
                    }
                }
            }
        });

        return query.then(mapApiResponseToBaseInfoForBannerCreationData);
    };

    public getVCards = (login: string, vcardIdIn: string[]) => {
        const query = this.createQuery<VCardsForBannersEdit>(vCardsQuery, {
            variables: {
                login,
                vcardIdIn
            }
        });

        return query.then(mapVCardsForEditQueryResultToQueryResponse);
    };

    public getMetroByCoordinates = (x: number, y: number) => {
        const query = this.createQuery<GetMetroByCoordinates>(getMetroByCoordinatesQuery, {
            variables: {
                coordinates: {
                    x,
                    y
                }
            }
        });

        return query.then(data => data.data);
    };

    public getByCoordinates = (x: number, y: number) => {
        const query = this.createQuery<GetGeoByCoordinates>(getGeoByCoordinatesQuery, {
            variables: {
                coordinates: {
                    x,
                    y
                }
            }
        });

        return query.then(data => data.data.getGeoByCoordinates);
    };

    public getGeoByAddress = (address: string) => {
        const query = this.createQuery<GetGeoByAddress>(getGeoByAddressQuery, {
            variables: {
                address: {
                    address
                }
            }
        });

        return query.then(data => data.data.getGeoByAddress);
    };

    public getLastChangedAds = (
        login: string,
        campaignId: CampaignId,
        group: EditedBannerGroup,
        measurerAccounts: MeasurerSystem[]
    ) => {
        const query = this.createQuery<GetLastChangedAds>(getLastChangedAdsQuery, {
            variables: {
                login,
                lastChangedAdsInput: {
                    items: bannerAdvTypesHasCopyPrevEditedBanner.map(bannerType => ({
                        campaignId: campaignId,
                        adType: bannerType
                    }))
                },
                placementsInput: {
                    placementType: 'OUTDOOR'
                }
            }
        });

        return query.then(res => mapApiResponseToBannersDataForCopyPrevEditedBanners(res, group, measurerAccounts));
    };

    public getImagesForGallery = (login: string, bannerType: AdvType | AdvTypeClient, fixedSize?: BannerImageSize) => {
        const query = this.createQuery<ImagesQueryForEdit>(imagesQueryForEdit, {
            variables: {
                login,
                cacheKey: '',
                filter: {
                    imageHashIn: [],
                    forAdType: bannerType === AdvTypeClient.TEXT_MOBILE ? AdvType.TEXT : bannerType,
                    width: fixedSize ? fixedSize.width : undefined,
                    height: fixedSize ? fixedSize.height : undefined
                },
                limitOffset: {
                    limit: 10000,
                    offset: 0
                }
            }
        });
        const type = mapBannerImageTypeForGalleryItems(bannerType);
        return query.then(data => mapImagesForEditQueryResultToQueryResponse(data, type));
    };

    public getCreativesForEdit = (login: string, creativeIdIn: string[], pageType?: CanvasPageType) => {
        let creativeTypeIn: GdCreativeTypeInput = GdCreativeTypeInput.CANVAS;

        switch (pageType) {
            case CanvasPageType.html5Add:
            case CanvasPageType.html5: {
                creativeTypeIn = GdCreativeTypeInput.HTML5_CREATIVE;

                break;
            }

            default: {
                creativeTypeIn = GdCreativeTypeInput.CANVAS;
            }
        }

        const query = this.createQuery<CreativesQuery>(creativesQuery, {
            variables: {
                input: {
                    searchBy: {
                        login
                    },
                    limitOffset: {
                        limit: 10000,
                        offset: 0
                    },
                    cacheKey: '',
                    filter: {
                        creativeIdIn: creativeIdIn,
                        creativeTypeIn
                    }
                }
            }
        });
        return query.then(mapCreativeForEditQueryResultToQueryResponse);
    };

    public getPerformanceCreatives = (params: GetPerformanceCreativesParams) => {
        const { login, campaignId, groupId, limit, offset, order, idOrName } = params;

        return this.createQuery<PerformanceCreativesQuery>(performanceCreativesQuery, {
            variables: {
                login,
                adsInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.LAST_30DAYS
                    },
                    orderBy: [
                        {
                            field: GdAdGroupOrderByFieldInput.CAMPAIGN_ID,
                            order: OrderInput.DESC
                        }
                    ],
                    limitOffset: {
                        offset: 0,
                        limit: BANNERS_IN_GROUP_LIMIT
                    },
                    filter: {
                        campaignIdIn: [campaignId],
                        adGroupIdIn: [groupId],
                        archived: false
                    }
                },
                adGroupInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.LAST_30DAYS
                    },
                    orderBy: [
                        {
                            field: GdAdGroupOrderByFieldInput.CAMPAIGN_ID,
                            order: OrderInput.DESC
                        }
                    ],
                    limitOffset: {
                        offset: 0,
                        limit: 1 // ищем данные только по 1 выбранной группе объявлений
                    },
                    filter: {
                        campaignIdIn: [campaignId],
                        adGroupIdIn: [groupId],
                        archived: false
                    }
                },
                limit,
                offset,
                order,
                idOrName
            }
        });
    };

    public validateBannerPrice = (bannerPrice: BannerPrice[]): Promise<ApolloQueryResult<validateAdPrice>> => {
        const query = this.createQuery<validateAdPrice>(validatePrice, {
            variables: {
                bannerPrice: bannerPrice.map(mapToValidateBannerPriceInput)
            }
        });
        return query;
    };

    public getInternalBannersEditData = (
        login: string,
        bannersIds: BannerId[],
        campaignsIds: CampaignId[]
    ): Promise<InternalBannersEditData> => {
        const query = this.createQuery<InternalBannersQueryForEdit>(adsInternalQueryForEdit, {
            variables: {
                adsInput: mapToAdsApiInput(bannersIds, campaignsIds),
                login
            }
        });

        return query.then(mapApiResponseToInternalBannersData);
    };

    public getInternalTemplateResources = (
        login: string,
        entries: GdInternalTemplateInfoRequestEntryInput[]
    ): Promise<InternalTemplateResources> => {
        const query = this.createQuery<InternalTemplateResourcesQuery>(internalTemplateResourcesQuery, {
            variables: {
                login,
                entries
            }
        });

        return query.then(mapApiResponseToTemplateResources);
    };

    public getInternalPlaceTemplates = (placesIds: string[]): Promise<InternalPlaceTemplates> => {
        const query = this.createQuery<InternalTemplatePlacesQuery>(internalTemplatePlacesQuery, {
            variables: {
                placesIds: placesIds
            }
        });

        return query.then(mapApiResponseToPlaceTemplates);
    };

    public getAdsGroupEditRecommendations = (
        login: string,
        campaignIds: CampaignId[],
        groupIds: GroupId[]
    ): Promise<ApolloQueryResult<RecommendationsAdsGroupEditQuery>> => {
        const query = this.createQuery<RecommendationsAdsGroupEditQuery>(recommendationsAdsGroupEditQuery, {
            variables: {
                login,
                adGroupInput: {
                    statRequirements: {
                        preset: GdStatPresetInput.LAST_30DAYS
                    },
                    orderBy: [
                        {
                            field: GdAdGroupOrderByFieldInput.CAMPAIGN_ID,
                            order: OrderInput.DESC
                        }
                    ],
                    limitOffset: {
                        offset: 0,
                        limit: 10000
                    },
                    filter: {
                        campaignIdIn: campaignIds,
                        adGroupIdIn: groupIds
                    }
                }
            }
        });

        return query;
    };

    public getContentPromotionMetadata = (
        url: string,
        contentPromotionType: GdContentPromotionType
    ): Promise<ApolloQueryResult<GetContentPromotionMetadata>> => {
        const query = this.createQuery<GetContentPromotionMetadata>(getContentPromotionMetadata, {
            variables: {
                input: { url, contentPromotionType }
            }
        });
        return query;
    };
}
